import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Layers,
  Lock,
  Plus,
  SearchX,
  Users,
  X as XIcon,
} from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import {
  departmentList,
  spaceListResponse,
  workspaceListResponse,
} from '../lib/types'
import { SpaceCreateDialog } from './SpaceCreateDialog'
import { Button } from './ui/Button'

/**
 * Space Explorer — two-step department → spaces picker.
 *
 * TaskMagix is single-Workspace, so grouping by workspace made no sense.
 * The admin now drills by DEPARTMENT instead:
 *
 *   Step 1  — a grid of Department cards (plus a "Company-wide" card for
 *             org-wide Spaces). The admin picks which Department to enter.
 *   Step 2  — slides in to show that Department's Spaces as cards; click
 *             one to open it, or "+ New Space" to add one to this
 *             Department. A back arrow returns to the Department grid.
 *
 * Data (all scoped to the company's one Workspace — the primary entry of
 * `GET /workspaces/mine`):
 *   • Departments — `GET /workspaces/:id/departments` (active only).
 *   • Spaces      — `GET /workspaces/:id/spaces`, grouped client-side by
 *     `departmentId` (null → the Company-wide bucket). Keys reuse
 *     `['spaces', workspaceId]` so SpaceCreateDialog's invalidation lands
 *     here instantly.
 *
 * Body-scroll is locked while open; Esc / backdrop click closes.
 */

const ORG_BUCKET = '__org'

/** Lowercased substring match. Empty query → always true. */
function matches(text, q) {
  if (!q) return true
  return String(text ?? '').toLowerCase().includes(q.toLowerCase())
}

/** Inline-highlight the matching substring with a <mark>. */
function Highlight({ text, query }) {
  const t = String(text ?? '')
  const q = String(query ?? '').trim()
  if (!q) return t
  const i = t.toLowerCase().indexOf(q.toLowerCase())
  if (i === -1) return t
  return (
    <>
      {t.slice(0, i)}
      <mark className="rounded px-0.5 bg-[color:var(--color-warning-bg)] text-[color:var(--color-ink-strong)]">
        {t.slice(i, i + q.length)}
      </mark>
      {t.slice(i + q.length)}
    </>
  )
}

/** Coloured letter avatar for Space cards. */
function SpaceAvatar({ name, color, size = 32 }) {
  const letter = (String(name ?? '?')[0] || '?').toUpperCase()
  return (
    <span
      className="inline-grid place-items-center rounded-lg text-white font-semibold shrink-0"
      style={{
        background: color || 'var(--color-accent-dim, #02367B)',
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${Math.round(size * 0.45)}px`,
      }}
      aria-hidden="true"
    >
      {letter}
    </span>
  )
}

export function SpacesModal({ open, onClose }) {
  const navigate = useNavigate()
  // step 1 = Department grid, step 2 = Spaces of the selected Department.
  const [step, setStep] = useState(1)
  // The Department (or Company-wide bucket) the admin drilled into. Kept
  // set during the slide-back so step 2 doesn't flash empty mid-animation;
  // cleared on close.
  const [selectedDept, setSelectedDept] = useState(null)
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  // Collapsible "Archived spaces" restore list at the bottom of step 1.
  const [showArchived, setShowArchived] = useState(false)
  const qc = useQueryClient()

  // ── Workspace (the company's one) ───────────────────────────────
  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () => {
      const res = await api.get('/workspaces/mine')
      return workspaceListResponse.parse(res.data)
    },
    enabled: open,
    staleTime: 30_000,
  })
  const workspace = workspacesQuery.data?.data?.[0] ?? null
  const workspaceId = workspace?.id ?? null

  // ── Departments + Spaces for that workspace ─────────────────────
  const departmentsQuery = useQuery({
    queryKey: ['departments', workspaceId],
    queryFn: async () => {
      const res = await api.get(`/workspaces/${workspaceId}/departments`)
      return departmentList.parse(res.data)
    },
    enabled: open && Boolean(workspaceId),
    staleTime: 30_000,
  })
  const spacesQuery = useQuery({
    queryKey: ['spaces', workspaceId],
    queryFn: async () => {
      const res = await api.get(`/workspaces/${workspaceId}/spaces`)
      return spaceListResponse.parse(res.data)
    },
    enabled: open && Boolean(workspaceId),
    staleTime: 30_000,
  })

  const departments = useMemo(
    () =>
      (departmentsQuery.data?.data ?? []).filter((d) => d.status === 'active'),
    [departmentsQuery.data],
  )
  // Personal ("My Space") spaces are excluded from Space Explorer — they live
  // only in the dedicated My Space sidebar section.
  const spaces = useMemo(
    () =>
      (spacesQuery.data?.data ?? []).filter(
        (s) => !s.isArchived && !s.isPersonal,
      ),
    [spacesQuery.data],
  )
  // Archived spaces are returned by the same endpoint; surface them in a
  // collapsible "Archived spaces" list so they can be restored (archive
  // otherwise hides them everywhere with no way back).
  const archivedSpaces = useMemo(
    () =>
      (spacesQuery.data?.data ?? []).filter(
        (s) => s.isArchived && !s.isPersonal,
      ),
    [spacesQuery.data],
  )

  /**
   * [Space Explorer exception] Personal Spaces of COMPANY-WIDE members, shown
   * to a Company Admin / authorised Sub Admin in their own dedicated section.
   *
   * Deliberately NOT folded into the department grid: a Personal Space has
   * `departmentId: null`, and a company-wide member has no department either,
   * so there is no department card they could honestly belong to.
   *
   * The server decides WHO appears here — it only returns another user's
   * Personal Space to an authorised admin, and stamps `ownerName` on exactly
   * those rows. The viewer's OWN Personal Space is excluded (it already lives
   * in the sidebar's "Personal Spaces" tree). These open READ-ONLY
   * (`myAccessLevel: 'view'`) and can never be shared.
   */
  const adminPersonalSpaces = useMemo(
    () =>
      (spacesQuery.data?.data ?? []).filter(
        (s) => s.isPersonal && !s.isArchived && Boolean(s.ownerName),
      ),
    [spacesQuery.data],
  )
  const filteredPersonalSpaces = useMemo(
    () =>
      adminPersonalSpaces.filter(
        (s) => matches(s.name, search) || matches(s.ownerName ?? '', search),
      ),
    [adminPersonalSpaces, search],
  )

  const unarchiveMutation = useMutation({
    mutationFn: async (spaceId) => {
      await api.patch(`/workspaces/${workspaceId}/spaces/${spaceId}`, {
        isArchived: false,
      })
    },
    onSuccess: () => {
      toast.success('Space restored successfully.')
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Group Spaces by Department (null → the Company-wide bucket).
  const spacesByDept = useMemo(() => {
    const map = new Map()
    for (const s of spaces) {
      const key = s.departmentId ? String(s.departmentId) : ORG_BUCKET
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(s)
    }
    return map
  }, [spaces])

  // Department cards. The Space Explorer is department-only — there is no
  // "Company-wide" card by design.
  const deptCards = useMemo(
    () =>
      departments.map((d) => ({
        id: String(d.id),
        name: d.name,
        kind: 'department',
        count: (spacesByDept.get(String(d.id)) ?? []).length,
        memberCount: d.memberCount ?? 0,
      })),
    [departments, spacesByDept],
  )

  const filteredDeptCards = useMemo(
    () => deptCards.filter((c) => matches(c.name, search)),
    [deptCards, search],
  )

  const deptSpaces = useMemo(() => {
    if (!selectedDept) return []
    const rows = spacesByDept.get(selectedDept.id) ?? []
    return rows.filter((s) => matches(s.name, search))
  }, [selectedDept, spacesByDept, search])

  // Archived spaces belonging to the SELECTED department — shown inside the
  // department view (step 2) so it's clear which department a restored space
  // returns to, rather than a single tenant-wide archived bucket.
  const deptArchivedSpaces = useMemo(() => {
    if (!selectedDept) return []
    return archivedSpaces.filter(
      (s) => String(s.departmentId ?? '') === selectedDept.id,
    )
  }, [selectedDept, archivedSpaces])

  // Footer count = Spaces shown across departments (org-wide Spaces aren't
  // surfaced in this explorer, so they're excluded for an honest total).
  const totalSpaces = deptCards.reduce((n, c) => n + c.count, 0)
  const loading =
    workspacesQuery.isLoading ||
    departmentsQuery.isLoading ||
    spacesQuery.isLoading

  // ── Navigation between steps ────────────────────────────────────
  function enterDept(card) {
    setSelectedDept(card)
    setSearch('')
    setStep(2)
  }
  function backToDepartments() {
    setStep(1)
    setSearch('')
    // selectedDept stays set so step 2 keeps its content during the slide.
  }
  function openSpace(id) {
    navigate(`/app/spaces/${id}`)
    onClose()
  }

  // ── Body-scroll lock + Esc ──────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (step === 2) backToDepartments()
        else onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose, step])

  // Reset to a clean step-1 grid whenever the modal closes.
  useEffect(() => {
    if (!open) {
      setStep(1)
      setSelectedDept(null)
      setSearch('')
      setCreateOpen(false)
      setShowArchived(false)
    }
  }, [open])

  if (!open) return null

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="spaces-modal-title"
        className="w-full max-w-[600px] flex flex-col rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
      >
        {/* HEADER — the popup is always titled "Space Explorer". The
         *  heading and the breadcrumb root both read "Space Explorer",
         *  with the selected Department as the breadcrumb's current crumb. */}
        <div className="px-6 py-4 border-b border-[color:var(--color-rule)] flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            {step === 2 ? (
              <button
                type="button"
                onClick={backToDepartments}
                aria-label="Back to Space Explorer"
                title="Back to Space Explorer"
                className="mt-0.5 h-8 w-8 grid place-items-center rounded-lg text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer shrink-0"
              >
                <ArrowLeft size={18} />
              </button>
            ) : (
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] shrink-0">
                <Layers size={20} />
              </span>
            )}
            <div className="min-w-0">
              <h2
                id="spaces-modal-title"
                className="font-semibold text-xl text-[color:var(--color-ink-strong)] leading-tight m-0 truncate"
              >
                Space Explorer
              </h2>
              {step === 2 ? (
                <nav
                  aria-label="Breadcrumb"
                  className="mt-1 flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]"
                >
                  <button
                    type="button"
                    onClick={backToDepartments}
                    className="hover:text-[color:var(--color-ink-strong)] cursor-pointer"
                  >
                    Space Explorer
                  </button>
                  <ChevronRight
                    size={14}
                    aria-hidden="true"
                    className="shrink-0"
                  />
                  <span className="font-medium text-[color:var(--color-ink-strong)] truncate">
                    {selectedDept?.name}
                  </span>
                </nav>
              ) : (
                <p className="mt-1 text-sm text-[color:var(--color-ink-muted)] truncate">
                  Choose a department to browse its Spaces.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            className="h-9 w-9 grid place-items-center rounded-lg text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer shrink-0"
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* DEPARTMENT TOOLBAR — step 2 only: the Department name on the
         *  left, the create action on the right (below the divider). */}
        {step === 2 ? (
          <div className="px-6 py-3 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex items-center justify-between gap-3 shrink-0">
            <div className="min-w-0">
              <h3 className="font-semibold text-base text-[color:var(--color-ink-strong)] truncate">
                {selectedDept?.name}
              </h3>
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                {deptSpaces.length} Space{deptSpaces.length === 1 ? '' : 's'}
              </p>
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus size={14} />
              New Space
            </Button>
          </div>
        ) : null}

        {/* SLIDING BODY — two panels on a horizontal track. */}
        <div className="relative overflow-hidden h-[min(520px,60vh)]">
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ transform: step === 2 ? 'translateX(-100%)' : 'translateX(0)' }}
          >
            {/* PANEL 1 — Department grid */}
            <div className="w-full shrink-0 h-full overflow-y-auto px-6 py-4 [scrollbar-width:thin]">
              {loading ? (
                <p className="px-1 py-12 text-center text-sm text-[color:var(--color-ink-muted)]">
                  Loading departments…
                </p>
              ) : workspacesQuery.isError ||
                departmentsQuery.isError ||
                spacesQuery.isError ? (
                <p className="px-1 py-12 text-center text-sm text-[color:var(--color-priority-urgent)]">
                  Could not load departments. Please try again.
                </p>
              ) : filteredDeptCards.length === 0 ? (
                search ? (
                  <EmptyState
                    icon={<SearchX size={22} />}
                    title="No matches"
                    hint="Try a different search term."
                  />
                ) : (
                  <EmptyState
                    icon={<Users size={22} />}
                    title="No departments yet"
                    hint="Create a department to organise your Spaces."
                  />
                )
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredDeptCards.map((card) => (
                    <DepartmentCard
                      key={card.id}
                      card={card}
                      query={search}
                      onEnter={() => enterDept(card)}
                    />
                  ))}
                </div>
              )}

              {/* Personal Spaces — company-wide members', visible to admins
               *  only. Separate from the department grid by design (see
               *  `adminPersonalSpaces`). Read-only on open. */}
              {filteredPersonalSpaces.length > 0 ? (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                      Personal Spaces
                    </h3>
                    <span className="text-xs text-[color:var(--color-ink-muted)]">
                      {filteredPersonalSpaces.length}
                    </span>
                  </div>
                  <p className="text-xs text-[color:var(--color-ink-muted)] mb-3">
                    Company-wide members&rsquo; personal spaces. Read-only.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {filteredPersonalSpaces.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => openSpace(s.id)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:bg-[color:var(--color-card-hover)] text-left cursor-pointer min-w-0"
                      >
                        <span className="shrink-0 text-[color:var(--color-ink-muted)]">
                          <Layers size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-[color:var(--color-ink-strong)] truncate">
                            {s.ownerName}
                          </span>
                          <span className="block text-xs text-[color:var(--color-ink-muted)] truncate">
                            {s.name}
                          </span>
                        </span>
                        <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)]">
                          Read-only
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {/* PANEL 2 — Spaces of the selected Department */}
            <div className="w-full shrink-0 h-full px-6 py-4 flex flex-col [scrollbar-width:thin]">
              {selectedDept ? (
                <>
                  {/* Spaces region — grows to fill, so the empty-state CTA
                   *  sits centred and the Archived strip stays at the bottom. */}
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    {deptSpaces.length === 0 ? (
                      <div className="h-full flex items-center justify-center">
                        <EmptyState
                          icon={<Layers size={22} />}
                          title={
                            search
                              ? 'No matching Spaces'
                              : `No Spaces in ${selectedDept.name} yet`
                          }
                          hint={
                            search
                              ? 'Try a different search term.'
                              : 'Use the New Space button above to add the first Space for this department.'
                          }
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {deptSpaces.map((sp) => (
                          <SpaceCard
                            key={sp.id}
                            space={sp}
                            query={search}
                            onOpen={() => openSpace(sp.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {/* This department's archived Spaces — pinned to the bottom.
                   *  Restoring one brings it back into THIS department, so it's
                   *  clear where it lands (unlike a tenant-wide archived bucket). */}
                  {!search && deptArchivedSpaces.length > 0 ? (
                    <div className="shrink-0">
                      <ArchivedSection
                        spaces={deptArchivedSpaces}
                        open={showArchived}
                        onToggle={() => setShowArchived((v) => !v)}
                        onRestore={(id) => unarchiveMutation.mutate(id)}
                        restoring={unarchiveMutation.isPending}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-3.5 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex items-center gap-5 text-sm text-[color:var(--color-ink-muted)] shrink-0">
          {loading ? (
            <span>Loading…</span>
          ) : (
            <>
              <span className="inline-flex items-center gap-2">
                <Users size={16} className="text-[color:var(--color-ink-subtle)]" />
                <span className="tabular-nums font-semibold text-[color:var(--color-ink-strong)]">
                  {departments.length}
                </span>
                department{departments.length === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-2">
                <Layers size={16} className="text-[color:var(--color-ink-subtle)]" />
                <span className="tabular-nums font-semibold text-[color:var(--color-ink-strong)]">
                  {totalSpaces}
                </span>
                space{totalSpaces === 1 ? '' : 's'}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Create a Space in the selected Department (null = company-wide). */}
      <SpaceCreateDialog
        open={createOpen}
        workspaceId={workspaceId}
        departmentId={selectedDept ? selectedDept.id : undefined}
        onClose={() => setCreateOpen(false)}
        onCreated={(newSpaceId) => {
          setCreateOpen(false)
          onClose()
          navigate(`/app/spaces/${newSpaceId}`)
        }}
      />
    </div>
  )

  return createPortal(modal, document.body)
}

// ── DepartmentCard ────────────────────────────────────────────────

function DepartmentCard({ card, query, onEnter }) {
  return (
    <button
      type="button"
      onClick={onEnter}
      className="group flex items-center gap-3 text-left rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 hover:border-[color:var(--color-accent)] hover:bg-[color:var(--color-card-hover)] hover:shadow-sm transition-all cursor-pointer"
    >
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl shrink-0 border bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]">
        <Users size={20} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-semibold text-[color:var(--color-ink-strong)]">
          <Highlight text={card.name} query={query} />
        </p>
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
          {card.memberCount} member{card.memberCount === 1 ? '' : 's'} ·{' '}
          {card.count} Space{card.count === 1 ? '' : 's'}
        </p>
      </div>
      <ChevronRight
        size={18}
        className="shrink-0 text-[color:var(--color-ink-subtle)] group-hover:text-[color:var(--color-accent-strong)] transition-colors"
      />
    </button>
  )
}

// ── ArchivedSection ───────────────────────────────────────────────
//
// Collapsible list of archived Spaces with a Restore action, shown at
// the bottom of the department grid. Without this, archiving a Space
// hides it everywhere with no way to bring it back.

function ArchivedSection({ spaces, open, onToggle, onRestore, restoring }) {
  return (
    <div className="mt-4 border-t border-[color:var(--color-rule)] pt-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-1 py-1.5 text-sm font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Archived spaces ({spaces.length})
      </button>
      {open ? (
        <div className="mt-1.5 space-y-1.5 max-h-44 overflow-y-auto [scrollbar-width:thin]">
          {spaces.map((sp) => (
            <div
              key={sp.id}
              className="flex items-center gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2"
            >
              <SpaceAvatar name={sp.name} color={sp.color} size={28} />
              <span className="flex-1 min-w-0 truncate text-sm text-[color:var(--color-ink)]">
                {sp.name}
              </span>
              <button
                type="button"
                onClick={() => onRestore(sp.id)}
                disabled={restoring}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-xs font-medium text-[color:var(--color-ink-strong)] hover:border-[color:var(--color-accent)] hover:text-[color:var(--color-accent-strong)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shrink-0"
              >
                <ArchiveRestore size={14} />
                Restore
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

// ── SpaceCard ─────────────────────────────────────────────────────

function SpaceCard({ space, query, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex items-center gap-3 text-left rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 hover:border-[color:var(--color-accent)] hover:bg-[color:var(--color-card-hover)] hover:shadow-sm transition-all cursor-pointer"
    >
      <SpaceAvatar name={space.name} color={space.color} size={36} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="min-w-0 truncate text-sm font-semibold text-[color:var(--color-ink-strong)]">
            <Highlight text={space.name} query={query} />
          </span>
          {/* Flag Private spaces only (visible to owner + invitees). Shared
           *  is the default, so it needs no badge. */}
          {space.isPrivate ? (
            <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-1.5 py-0.5 text-[10px] font-medium text-[color:var(--color-ink-subtle)]">
              <Lock size={10} />
              Private
            </span>
          ) : null}
        </div>
        {space.description ? (
          <p className="mt-0.5 truncate text-xs text-[color:var(--color-ink-muted)]">
            {space.description}
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-subtle)]">
            Open Space
          </p>
        )}
      </div>
      <ArrowRight
        size={16}
        className="shrink-0 text-[color:var(--color-ink-subtle)] opacity-0 group-hover:opacity-100 group-hover:text-[color:var(--color-accent-strong)] transition-all"
      />
    </button>
  )
}

// ── EmptyState ────────────────────────────────────────────────────

function EmptyState({ icon, title, hint, action = null }) {
  return (
    <div className="px-6 py-14 text-center">
      <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
        {icon}
      </div>
      <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
        {title}
      </p>
      {hint ? (
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">{hint}</p>
      ) : null}
      {action}
    </div>
  )
}
